// PackageDetails.jsx
import { useState, useEffect, useRef } from "react";
import { Box, CircularProgress, Divider } from "@mui/material";
import { SectionHeader } from "@/Components/Header/SectionsHeader";
import { LuX } from "@/Components/Icons/Icons";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import { useUser } from "@/Context/UserContext";
import TableGenerator from "@/Utils/FormGenerator/TableGenerator";
import InputTextField from "@/Components/Form/InputTextField";
import dayjs from "dayjs";
import DateRangeSelector from "@/Components/Form/DateRangeCalendar";
import {
  useGetImportBondTypeQuery,
  useLazyGetImportBondTypeQuery,
} from "@/redux/ExportPage/CreateNewJob/Shipment/ShipmentMainSlice";
import { hookMap } from "@/redux/hookMap";
import InputDropdown from "@/Components/Form/InputDropdown";

export default function ShipmentExBondInitPopUp({ onClose }) {
  const { setFormData, setValueGlobal } = useUser();
  const [importJob, setImportJob] = useState(null);
  const [jobNo, setJobNo] = useState(null);

  const [customerId, setCustomerId] = useState("");

  const [beNo, setBeNo] = useState(null);

  const [dateRange, setDateRange] = useState([null, null]);
  const endPoint = "getCustomers";
  const apiHook = hookMap[endPoint];
  const [bondType, setBondType] = useState(null);
  const { data } = apiHook();
  const {
    data: initialData,
    error: initialError,
    isFetching: initialFetching,
  } = useGetImportBondTypeQuery({});

  // 2️⃣ Manual search (lazy)
  const [
    triggerBondType,
    { data: searchData, error: searchError, isFetching: searchFetching },
  ] = useLazyGetImportBondTypeQuery();

  // 3️⃣ Combined unified values
  const BondDetails = searchData || initialData;
  const error = searchError || initialError;
  const isFetching = searchFetching || initialFetching;

  useEffect(() => {
    if (error) {
      setFormData((prev) => ({
        ...prev,
        BondDetails: [],
      }));
      return;
    }

    if (!isFetching && Array.isArray(BondDetails)) {
      const dataWithSrNo = BondDetails?.map((item, index) => ({
        ...item,
        srNo: index + 1,
        id: item?.jobId,
      }));

      setFormData((prev) => ({
        ...prev,
        BondDetails: dataWithSrNo,
      }));
    }
  }, [BondDetails, isFetching, error]);

  const handleInitialize = () => {
    if (!importJob) {
      return;
    }

    const convertToISODateOnly = (dateTimeStr) => {
      if (!dateTimeStr) return null;

      const [datePart] = dateTimeStr.split(" "); // ignore time
      const [dd, mm, yyyy] = datePart.split("-");

      return `${yyyy}-${mm}-${dd}`; // YYYY-MM-DD
    };

    const beDateValue = convertToISODateOnly(importJob?.beDate);
    const parsedDate = beDateValue ? dayjs(beDateValue).utc(true) : null;

    // Example mapping — adjust according to your schema
    setValueGlobal("exportShipmentExBondBENo", importJob?.beNo);
    setValueGlobal("exportShipmentExBondBEDate", parsedDate);
    setValueGlobal("exportShipmentExBondVessel", importJob?.vessal);

    setValueGlobal("exportShipmentExBondVoyage", importJob?.voyage || null);

    setValueGlobal("exportShipmentExBondIGMNo", importJob?.igmNo || null);
    setValueGlobal("exportShipmentExBondGMDate", importJob?.igmDate || null);
    setValueGlobal(
      "exportShipmentExBondNoOfPackage",
      Number(importJob?.noOfPackages) || null
    );
    setValueGlobal(
      "exportShipmentExBondNoOfPackageUnit",
      importJob?.packageType || null
    );

    onClose();
  };

  const bondTypeOptions = [
    { label: "InBond", value: "InBond" },
    { label: "ExBond", value: "ExBond" },
  ];

  const visibleFields = [
    {
      key: "srNo",
      label: "Sr No",
      width: 80,
    },
    { key: "jobNo", label: "Job No", width: 120 },
    { key: "beNo", label: "BE No", width: 120 },
    { key: "beType", label: "BE Type", width: 120 },
  ];

  const handlers = {
    handleRowClick: (row) => {
      if (!row) return;
      setImportJob(row);
    },
  };

  const resetFilters = () => {
    setDateRange([null, null]);
    setImportJob("");
    setBeNo("");
    setCustomerId("");
    setBondType("");
  };

  const handleSearch = async () => {
    const params = {};

    if (bondType) params.bondType = bondType;
    if (jobNo) params.jobNumber = jobNo;
    if (beNo) params.beNumber = beNo;
    if (customerId) params.customerId = customerId;

    if (dateRange?.from && dateRange?.to) {
      params.from = dayjs(dateRange[0]).format("YYYY-MM-DD");
      params.to = dayjs(dateRange[1]).format("YYYY-MM-DD");
    }

    try {
      const result = await triggerBondType(params).unwrap();
      const response = result?.data?.data.map((item, index) => ({
        ...item,
        srNo: index + 1,
        id: item?.jobId,
      }));
      setFormData((prev) => ({
        ...prev,
        BEDetails: response,
      }));
      resetFilters();
    } catch (error) {
      console.error("❌ Filter Error:", error);
      setFormData((prev) => ({
        ...prev,
        BEDetails: [],
      }));
    }
  };

  const handleClearFilters = () => {
    setJobNo("");
    setBeNo("");
    setCustomerId("");
    setBondType("");
    setDateRange([null, null]);

    triggerBondType({});
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--gray-0)",
        width: "100%",
        maxWidth: "100vw",
        height: 700,
        // maxHeight: 600,
        mb: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: 2,
          m: 0.5,
        }}
      >
        <SectionHeader title={"Job Details"} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            gap: 4,
            alignItems: "center",
          }}
        >
          <LuX size={20} style={{ cursor: "pointer" }} onClick={onClose} />
        </Box>
      </Box>
      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <Box
          sx={{
            border: "1px solid var(--gray-400)",
            borderRadius: 2,
            p: 2,
            m: 1,
            backgroundColor: "var(--gray-50)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {/* 🔹 Row 1 — Job No, BE No, Customer */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: 3,
              alignItems: "center",
            }}
          >
            <InputTextField
              name="jobNo"
              label="Job No"
              value={jobNo}
              onChange={(e) => setJobNo(e.target.value)}
              placeholder="Job No"
            />

            <InputTextField
              name="beNo"
              label="BE No"
              value={beNo}
              onChange={(e) => setBeNo(e.target.value)}
              placeholder="BE No"
            />

            <InputTextField
              name="customerName"
              type="autocomplete"
              options={data}
              label="Customer Name"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="Customer Name"
            />
            <InputDropdown
              label="Bond Type"
              value={bondType}
              onChange={(e) => setBondType(e)}
              options={bondTypeOptions}
              width="100%"
              gap={false}
            />
          </Box>

          {/* 🔹 Row 2 — Date Range, Bond Type, Search Button */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr auto auto",
              gap: 3,
              alignItems: "start",
            }}
          >
            <DateRangeSelector
              label="Working Period"
              value={dateRange}
              onChange={setDateRange}
              width="70%"
            />

            <OutLinedButton
              variant="outlined"
              color="secondary"
              label="Clear"
              sx={{ width: "120px" }}
              onClick={handleClearFilters}
            />

            <OutLinedButton
              variant="contained"
              color="secondary"
              label="Search"
              sx={{ width: "140px" }}
              onClick={handleSearch}
            />
          </Box>
        </Box>

        {/* Loader + Table */}
        {isFetching && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              width: "100%",
              gap: 1,
            }}
          >
            <CircularProgress size={40} sx={{ color: "var(--brand-600)" }} />
          </Box>
        )}

        {!isFetching && (
          <TableGenerator
            compKey={"BondDetails"}
            visibleFields={visibleFields}
            label={"Bond details"}
            handlers={handlers}
            showActions={false}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mx: 2,
          borderTop: "1px solid var(--gray-400)",
          mt: 2,
          gap: 2,
        }}
      >
        <BlackButton
          label="Initialize From Selected Job"
          sx={{
            mt: 2,
            width: "300px",
          }}
          onClick={handleInitialize}
          disabled={importJob?.length === 0}
        />
      </Box>
    </Box>
  );
}
